import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Coins, ShoppingBag, Award, Play, Gamepad2, Clock } from "lucide-react";
import AdminShell from "../components/admin/AdminShell";

const BASE = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ icon, label, value, bg, testId }) => (
  <div className="chunky-card p-3" style={{ background: bg }} data-testid={testId}>
    <div className="flex items-center gap-2" style={{ color: "#5F27CD" }}>
      {icon}
      <span className="text-xs uppercase font-display">{label}</span>
    </div>
    <div className="font-display text-2xl mt-1">{value}</div>
  </div>
);

const KIND_ICON = {
  page_view: "🧭",
  game_play: "🎮",
  coins_earned: "💰",
  coins_spent: "🛍️",
  order_placed: "📦",
  sticker_unlock: "🏆",
};

const AdminUserDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${BASE}/api/admin/users/${id}`, { credentials: "include" });
        if (r.status === 401 || r.status === 403) {
          nav("/admin", { replace: true });
          return;
        }
        if (r.status === 404) {
          setErr("Visitor not found");
          setLoading(false);
          return;
        }
        setData(await r.json());
      } catch {
        setErr("Failed to load");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, nav]);

  return (
    <AdminShell>
    <div data-testid="admin-user-detail">
      <div className="hidden md:flex items-center gap-2 px-4 sm:px-6 pt-4">
        <Link
          to="/admin/users"
          className="btn-3d btn-sm"
          aria-label="Back"
          data-testid="admin-user-back"
        >
          <ArrowLeft size={16} strokeWidth={3} /> Back to users
        </Link>
        <span className="font-display text-xl">Visitor detail</span>
      </div>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
        {loading && <div className="text-center py-8">Loading…</div>}
        {err && (
          <div className="chunky-card p-4" style={{ background: "#FF5A5F22", color: "#FF5A5F" }} data-testid="admin-user-error">
            {err}
          </div>
        )}
        {data && (
          <>
            <div className="chunky-card p-4 mb-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-display text-xl" data-testid="admin-user-name">
                    {data.visitor.last_name || data.visitor.first_name || "Unnamed visitor"}
                  </div>
                  <div className="text-xs font-mono" style={{ color: "#484848" }}>
                    {data.visitor.visitor_id}
                  </div>
                </div>
                <div className="text-sm" style={{ color: "#484848" }}>
                  {(data.visitor.first_seen || "").slice(0, 16).replace("T", " ")} → {(data.visitor.last_seen || "").slice(0, 16).replace("T", " ")}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm mt-3" style={{ color: "#484848" }}>
                <div>📱 Device: <b data-testid="detail-device">{data.visitor.device || "—"}</b></div>
                <div>🌐 Language: <b>{data.visitor.language || "—"}</b></div>
                <div>🌍 Country: <b data-testid="detail-country">{data.visitor.country_name || data.visitor.country || "—"}</b></div>
                <div>🏙️ City: <b data-testid="detail-city">{(() => {
                  const parts = [];
                  [data.visitor.city, data.visitor.region].forEach((p) => {
                    const t = (p || "").trim();
                    if (t && !parts.includes(t)) parts.push(t);
                  });
                  return parts.join(", ") || "—";
                })()}</b></div>
                <div>🎂 DOB: <b data-testid="detail-dob">{data.visitor.dob || "—"}</b></div>
                <div>🕒 TZ: <b>{data.visitor.tz || "—"}</b></div>
                <div>📐 Screen: <b>{data.visitor.screen?.w || "?"}×{data.visitor.screen?.h || "?"}</b></div>
                <div>🔁 Referrer: <b>{data.visitor.first_referrer || "direct"}</b></div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
              <Stat icon={<Play size={18} strokeWidth={3} />} label="Visits" value={data.visitor.visit_count || 0} bg="#FFE0F5" testId="detail-stat-visits" />
              <Stat icon={<Gamepad2 size={18} strokeWidth={3} />} label="Plays" value={data.visitor.total_plays || 0} bg="#E5F6FF" testId="detail-stat-plays" />
              <Stat
                icon={<Clock size={18} strokeWidth={3} />}
                label="Playtime"
                value={(() => {
                  const s = data.visitor.playtime_seconds || 0;
                  const m = Math.floor(s / 60);
                  return `${m}m ${(s % 60).toString().padStart(2, "0")}s`;
                })()}
                bg="#EFE7FF"
                testId="detail-stat-playtime"
              />
              <Stat icon={<Coins size={18} strokeWidth={3} />} label="Coins earned" value={data.visitor.coins_earned || 0} bg="#FFF3D6" testId="detail-stat-coins-earned" />
              <Stat icon={<ShoppingBag size={18} strokeWidth={3} />} label="Orders" value={data.visitor.orders_placed || 0} bg="#E9FFE6" testId="detail-stat-orders" />
              <Stat icon={<Award size={18} strokeWidth={3} />} label="Stickers" value={data.visitor.stickers_unlocked || 0} bg="#FFECEC" testId="detail-stat-stickers" />
            </div>

            <div className="chunky-card p-4" data-testid="admin-user-events">
              <h2 className="font-display text-lg mb-2">Last 100 events</h2>
              {data.events.length === 0 ? (
                <p className="text-sm" style={{ color: "#484848" }}>No events yet.</p>
              ) : (
                <ul className="text-sm">
                  {data.events.map((e, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 py-1"
                      style={{ borderBottom: "1px dashed #eee" }}
                      data-testid={`admin-user-event-${i}`}
                    >
                      <span className="text-lg" aria-hidden="true">
                        {KIND_ICON[e.kind] || "•"}
                      </span>
                      <span className="font-display" style={{ minWidth: 120 }}>
                        {e.kind}
                      </span>
                      <span style={{ color: "#484848", flex: 1 }}>
                        {e.game_id ? `game: ${e.game_id}` : ""}
                        {e.coins ? ` · ${e.coins} coins` : ""}
                        {e.value ? ` · value ${e.value}` : ""}
                        {e.path ? ` · ${e.path}` : ""}
                      </span>
                      <span className="text-xs font-mono" style={{ color: "#8a8a8a" }}>
                        {(e.at || "").slice(0, 16).replace("T", " ")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </main>
    </div>
    </AdminShell>
  );
};

export default AdminUserDetail;
